import React, { useEffect, useMemo, useState } from "react";
import { Plus, Search, Trash2, X } from "lucide-react";
import apiClient from "../../../utils/apiClient";
import {
  AppButton, AppCard, AppCardContent, AppCardDescription, AppCardHeader, AppCardTitle,
  Callout, Checkbox, FieldGroup, FormField, IconButton, Input, NativeSelect, Radio,
} from "../../ui";

const AUDIENCES = [
  { value: "batch", label: "Batches", description: "Students in the batches you choose." },
  { value: "course", label: "Courses", description: "Everyone enrolled in a course, in any batch." },
  { value: "direct", label: "Specific students", description: "Search for students and pick them." },
  { value: "open", label: "Every student", description: "Everyone in your institute, across branches." },
  { value: "rules", label: "Different rules per group", description: "Own dates, attempts or pass mark for each batch or course." },
];

const studentName = (s) => s.name || s.fullName || s.email || "Student";

/** A searchable list of checkboxes — batches or courses. */
function ChecklistField({ legend, hint, items, selected, onChange, labelOf, detailOf, emptyText, disabled }) {
  const [filter, setFilter] = useState("");
  const shown = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return f ? items.filter((i) => labelOf(i).toLowerCase().includes(f)) : items;
  }, [items, filter, labelOf]);
  const set = new Set(selected);
  const allShown = shown.length > 0 && shown.every((i) => set.has(i._id));

  return (
    <FieldGroup legend={legend} hint={hint}>
      {items.length === 0 ? (
        <p className="tw-m-0 tw-text-[0.8125rem] tw-text-content-muted">{emptyText}</p>
      ) : (
        <>
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            <div className="tw-min-w-[14rem] tw-flex-1">
              <Input
                type="search"
                aria-label={`Filter ${legend.toLowerCase()}`}
                placeholder={`Filter ${legend.toLowerCase()}`}
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                leading={<Search size={16} />}
              />
            </div>
            <AppButton
              type="button"
              size="sm"
              variant="outline"
              disabled={disabled || shown.length === 0}
              onClick={() => onChange(allShown
                ? selected.filter((id) => !shown.some((i) => i._id === id))
                : [...new Set([...selected, ...shown.map((i) => i._id)])])}
            >
              {allShown ? "Clear these" : `Select ${filter ? "these" : "all"} (${shown.length})`}
            </AppButton>
            <span className="tw-text-[0.8125rem] tw-text-content-muted">{selected.length} selected</span>
          </div>
          <ul className="tw-m-0 tw-grid tw-max-h-80 tw-list-none tw-gap-1 tw-overflow-y-auto tw-rounded-md tw-border tw-border-solid tw-border-line tw-p-2 min-[768px]:tw-grid-cols-2">
            {shown.map((i) => (
              <li key={i._id} className="tw-rounded-sm tw-px-2 tw-py-1.5 hover:tw-bg-surface-hover">
                <Checkbox
                  checked={set.has(i._id)}
                  onChange={(e) => onChange(e.target.checked ? [...selected, i._id] : selected.filter((x) => x !== i._id))}
                  label={labelOf(i)}
                  description={detailOf?.(i)}
                  disabled={disabled}
                />
              </li>
            ))}
          </ul>
        </>
      )}
    </FieldGroup>
  );
}

/** Pick students by searching — the list of everyone is never loaded. */
function StudentPicker({ selected, labels, onChange, onLabel, disabled }) {
  const [text, setText] = useState("");
  const [results, setResults] = useState([]);
  const [state, setState] = useState("idle");

  useEffect(() => {
    const q = text.trim();
    if (q.length < 2) { setResults([]); setState("idle"); return undefined; }
    let alive = true;
    setState("loading");
    const t = setTimeout(() => {
      apiClient.get("/students/search", { params: { text: q }, headers: { "X-Branch-Id": "all" } })
        .then(({ data }) => { if (alive) { setResults(data.students || []); setState("idle"); } })
        .catch(() => { if (alive) { setResults([]); setState("error"); } });
    }, 350);
    return () => { alive = false; clearTimeout(t); };
  }, [text]);

  const set = new Set(selected);
  return (
    <FieldGroup legend="Students" hint="Search by name, email or mobile.">
      <Input
        type="search"
        aria-label="Search students"
        placeholder="Type at least two letters"
        value={text}
        onChange={(e) => setText(e.target.value)}
        leading={<Search size={16} />}
        disabled={disabled}
      />
      {selected.length > 0 && (
        <ul className="tw-m-0 tw-flex tw-list-none tw-flex-wrap tw-gap-1.5 tw-p-0" aria-label="Chosen students">
          {selected.map((id) => (
            <li key={id} className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-primary-soft tw-py-0.5 tw-pl-3 tw-pr-1 tw-text-[0.8125rem] tw-text-primary-soft-fg">
              {labels[id] || "Student"}
              <IconButton icon={X} size="xs" label={`Remove ${labels[id] || "student"}`} onClick={() => onChange(selected.filter((x) => x !== id))} disabled={disabled} />
            </li>
          ))}
        </ul>
      )}
      {state === "loading" && <p className="tw-m-0 tw-text-[0.8125rem] tw-text-content-muted">Searching…</p>}
      {state === "error" && <Callout tone="danger">Students could not be searched. Try again.</Callout>}
      {state === "idle" && text.trim().length >= 2 && results.length === 0 && (
        <p className="tw-m-0 tw-text-[0.8125rem] tw-text-content-muted">No students match.</p>
      )}
      {results.length > 0 && (
        <ul className="tw-m-0 tw-flex tw-max-h-72 tw-list-none tw-flex-col tw-gap-1 tw-overflow-y-auto tw-rounded-md tw-border tw-border-solid tw-border-line tw-p-2">
          {results.map((s) => (
            <li key={s._id} className="tw-rounded-sm tw-px-2 tw-py-1.5 hover:tw-bg-surface-hover">
              <Checkbox
                checked={set.has(s._id)}
                onChange={(e) => {
                  onLabel(s._id, studentName(s));
                  onChange(e.target.checked ? [...selected, s._id] : selected.filter((x) => x !== s._id));
                }}
                label={studentName(s)}
                description={s.email || s.mobile || ""}
                disabled={disabled}
              />
            </li>
          ))}
        </ul>
      )}
    </FieldGroup>
  );
}

const blankAssignmentRule = () => ({ targetType: "batch", target: "", startDate: "", endDate: "", maxAttempts: "", passingPercentage: "" });

/** One group's own dates, attempts and pass mark. */
function AssignmentRules({ rules, onChange, batches, courses, disabled }) {
  const update = (i, patch) => onChange(rules.map((r, j) => (j === i
    ? { ...r, ...patch, ...(patch.targetType !== undefined ? { target: "" } : {}) }
    : r)));
  return (
    <FieldGroup legend="Groups" hint="Leave a field empty to use the assessment's own setting for that group.">
      {rules.map((r, i) => {
        const targets = r.targetType === "course" ? courses : batches;
        return (
          <div key={i} className="tw-flex tw-flex-col tw-gap-3 tw-rounded-md tw-border tw-border-solid tw-border-line tw-p-3">
            <div className="tw-grid tw-items-end tw-gap-2 min-[768px]:tw-grid-cols-[9rem_minmax(0,1fr)_auto]">
              <FormField label="Kind">
                <NativeSelect value={r.targetType} onChange={(e) => update(i, { targetType: e.target.value })} disabled={disabled}>
                  <option value="batch">Batch</option>
                  <option value="course">Course</option>
                </NativeSelect>
              </FormField>
              <FormField label={r.targetType === "course" ? "Course" : "Batch"}>
                <NativeSelect value={r.target} onChange={(e) => update(i, { target: e.target.value })} disabled={disabled}>
                  <option value="">Choose…</option>
                  {targets.map((t) => <option key={t._id} value={t._id}>{t.name || t.title}</option>)}
                </NativeSelect>
              </FormField>
              <IconButton icon={Trash2} label={`Remove group ${i + 1}`} variant="ghost-destructive" onClick={() => onChange(rules.filter((_, j) => j !== i))} disabled={disabled} />
            </div>
            <div className="tw-grid tw-gap-2 min-[768px]:tw-grid-cols-4">
              <FormField label="Opens">
                <Input type="datetime-local" value={r.startDate} onChange={(e) => update(i, { startDate: e.target.value })} disabled={disabled} />
              </FormField>
              <FormField label="Closes">
                <Input type="datetime-local" value={r.endDate} onChange={(e) => update(i, { endDate: e.target.value })} disabled={disabled} />
              </FormField>
              <FormField label="Attempts">
                <Input type="number" min={0} placeholder="Same" value={r.maxAttempts} onChange={(e) => update(i, { maxAttempts: e.target.value })} disabled={disabled} />
              </FormField>
              <FormField label="Pass mark %">
                <Input type="number" min={0} max={100} placeholder="Same" value={r.passingPercentage} onChange={(e) => update(i, { passingPercentage: e.target.value })} disabled={disabled} />
              </FormField>
            </div>
          </div>
        );
      })}
      <div>
        <AppButton type="button" size="sm" variant="outline" onClick={() => onChange([...rules, blankAssignmentRule()])} disabled={disabled}>
          <Plus size={16} aria-hidden="true" /> Add a group
        </AppButton>
      </div>
    </FieldGroup>
  );
}

/**
 * Step 5 — who takes the assessment and when; on the platform library, its
 * marketplace price instead. Switching between kinds keeps what was ticked
 * under each, so trying "Courses" and going back loses no batches; only the
 * kind showing is saved.
 */
export default function AudienceStep({
  form, set, setForm, batches, courses, studentLabels, onStudentLabel, isLibrary, locked, errors,
}) {
  if (isLibrary) {
    const pricing = form.pricing;
    const setPricing = (patch) => setForm((f) => ({ ...f, pricing: { ...f.pricing, ...patch } }));
    return (
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>Marketplace pricing</AppCardTitle>
          <AppCardDescription>Institutes import this assessment from the marketplace. Coding problems are copied into their coding bank.</AppCardDescription>
        </AppCardHeader>
        <AppCardContent className="tw-flex tw-flex-col tw-gap-4">
          <div className="tw-grid tw-gap-3 min-[768px]:tw-grid-cols-2">
            <Radio variant="card" name="pricing" checked={pricing.model === "free"} onChange={() => setPricing({ model: "free" })} label="Free" description="Any institute with quizzes can import it." />
            <Radio variant="card" name="pricing" checked={pricing.model === "paid"} onChange={() => setPricing({ model: "paid" })} label="Paid" description="Institutes need access before importing." />
          </div>
          {pricing.model === "paid" && (
            <div className="tw-grid tw-gap-3 min-[768px]:tw-grid-cols-3">
              <FormField label="Billing period">
                <NativeSelect value={pricing.period} onChange={(e) => setPricing({ period: e.target.value })}>
                  <option value="">Choose…</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                  <option value="lifetime">Lifetime</option>
                </NativeSelect>
              </FormField>
              <FormField label="Amount">
                <Input type="number" min={0} value={pricing.amount} onChange={(e) => setPricing({ amount: Number(e.target.value) || 0 })} />
              </FormField>
              <FormField label="Currency">
                <Input value={pricing.currency} maxLength={3} onChange={(e) => setPricing({ currency: e.target.value.toUpperCase() })} />
              </FormField>
            </div>
          )}
        </AppCardContent>
      </AppCard>
    );
  }

  const branchOf = (b) => b.branchId?.name || b.branch?.name || b.branchName || "";

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>Who takes it</AppCardTitle>
          <AppCardDescription>Shared across all your branches — choose any batch or course. Students are told when you publish.</AppCardDescription>
        </AppCardHeader>
        <AppCardContent className="tw-flex tw-flex-col tw-gap-5">
          <fieldset className="tw-m-0 tw-grid tw-min-w-0 tw-gap-3 tw-border-0 tw-p-0 min-[768px]:tw-grid-cols-2">
            <legend className="tw-sr-only">Who takes it</legend>
            {AUDIENCES.map((a) => (
              <Radio
                key={a.value}
                variant="card"
                name="audience"
                checked={form.assignmentType === a.value}
                onChange={() => set("assignmentType", a.value)}
                disabled={locked}
                label={a.label}
                description={a.description}
              />
            ))}
          </fieldset>

          {form.assignmentType === "batch" && (
            <ChecklistField
              legend="Batches"
              items={batches}
              selected={form.assignedBatches}
              onChange={(ids) => set("assignedBatches", ids)}
              labelOf={(b) => b.name || "Batch"}
              detailOf={(b) => [branchOf(b), b.status === "Completed" ? "Completed" : ""].filter(Boolean).join(" · ")}
              emptyText="No batches yet. Create a batch first, or choose another option."
              disabled={locked}
            />
          )}
          {form.assignmentType === "course" && (
            <ChecklistField
              legend="Courses"
              items={courses}
              selected={form.assignedCourses}
              onChange={(ids) => set("assignedCourses", ids)}
              labelOf={(c) => c.title || c.name || "Course"}
              emptyText="No courses yet."
              disabled={locked}
            />
          )}
          {form.assignmentType === "direct" && (
            <StudentPicker
              selected={form.assignedStudents}
              labels={studentLabels}
              onLabel={onStudentLabel}
              onChange={(ids) => set("assignedStudents", ids)}
              disabled={locked}
            />
          )}
          {form.assignmentType === "open" && (
            <Callout tone="info">Every student in your institute, across every branch, will see this assessment.</Callout>
          )}
          {form.assignmentType === "rules" && (
            <AssignmentRules
              rules={form.assignmentRules}
              onChange={(rules) => set("assignmentRules", rules)}
              batches={batches}
              courses={courses}
              disabled={locked}
            />
          )}
          {errors.audience && <Callout tone="danger" role="alert">{errors.audience}</Callout>}
        </AppCardContent>
      </AppCard>

      <AppCard>
        <AppCardHeader>
          <AppCardTitle>When</AppCardTitle>
          <AppCardDescription>Leave both empty to keep it open from the moment you publish.</AppCardDescription>
        </AppCardHeader>
        <AppCardContent className="tw-grid tw-gap-4 min-[768px]:tw-grid-cols-2">
          <FormField label="Opens" hint="Students can start from this time.">
            <Input type="datetime-local" size="lg" value={form.startDate} onChange={(e) => set("startDate", e.target.value)} disabled={locked} />
          </FormField>
          <FormField label="Closes" error={errors.endDate} hint="No new attempts after this time.">
            <Input type="datetime-local" size="lg" value={form.endDate} onChange={(e) => set("endDate", e.target.value)} />
          </FormField>
        </AppCardContent>
      </AppCard>
    </div>
  );
}
